# Route: /tracking
# Operator tracking dashboard: at-a-glance status and quick actions
# Permission: TrackingRecord:read

page-tracking-dashboard:
  type: Page
  archetype: dashboard
  meta:
    title: $t.tracking.dashboard.meta_title
    route: /tracking
    layout: shell
    auth: required
  data:
    dashboard:
      model: TrackingDashboard
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - tracking-dash-header
    - tracking-dash-btn-create
    - tracking-dash-landing
    - tracking-dash-stats-grid
    - tracking-dash-pending
    - tracking-dash-recent

# ── Header ───────────────────────────────────────────────────────────────────

tracking-dash-header:
  type: PageHeader
  content: $t.tracking.dashboard.title

# ── Trackings landing (F80 FR-4) ───────────────────────────────────────────────
# Pick the asset you work on → its declared forms → per-tracking links into
# the record stream (?contextId=&formName=) and record-create
# (?asset=&template=). Links carry returnTo=/tracking (F79).
tracking-dash-landing:
  type: Section
  permission: "TrackingRecord:read"
  a11y:
    label: $t.tracking.landing.section_label
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - tracking-dash-landing-title
    - tracking-dash-landing-widget

tracking-dash-landing-title:
  type: Text
  content: $t.tracking.landing.title
  tokens:
    font_size: $font.size.md
    font_weight: "600"

tracking-dash-landing-widget:
  type: Widget
  ref: xp87-tracking-landing

# Quick-create button. F75 #178 button-variant convention: this is the
# screen's single filled primary (READ page → action top-right); every other
# dashboard affordance is a quiet surface (stat cards + row navigation), so
# the page stays calm.
#
# M1 rendering sweep (phone-width) — used to carry `responsive.sm.visible:
# false`, hiding the one-tap "new entry" shortcut exactly where operators
# most need it (a phone, in the field). Visible at every width now;
# `align_self: flex-end` still keeps it right-aligned on its own row.
tracking-dash-btn-create:
  type: Button
  variant: filled
  color: info
  content: $t.tracking.dashboard.btn_new_record
  a11y:
    label: $t.tracking.dashboard.btn_new_record
  action:
    type: navigate
    route: /tracking/records/new
    # F79 — Cancel / on_success on the create screen returns to the dashboard.
    return_context: origin_only
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg
    align_self: flex-end

# ── Stats cards ──────────────────────────────────────────────────────────────

# Renamed `tracking-dash-stats` → `tracking-dash-stats-grid` so the Tier 5
# scenario can address the grid by id directly (assert-visible / overflow).
tracking-dash-stats-grid:
  type: Grid
  spatial:
    columns: 4
    gap: $spacing.md
    responsive:
      sm:
        columns: 2
  children:
    - tracking-dash-card-today
    - tracking-dash-card-pending
    - tracking-stat-review
    - tracking-stat-completed

tracking-dash-card-today:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.xs
    border_inline_start_accent: info
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.md
    border: $border.default
  children:
    - tracking-dash-card-today-count
    - tracking-dash-card-today-label

tracking-dash-card-today-count:
  type: Text
  bind:
    content: dashboard.counts.totalToday
  tokens:
    font_size: $font.size.2xl
    font_family: $font.family.mono

tracking-dash-card-today-label:
  type: Text
  content: $t.tracking.dashboard.records_today
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tracking-dash-card-pending:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.xs
    border_inline_start_accent: caution
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.md
    border: $border.default
  children:
    - tracking-dash-card-pending-figure
    - tracking-dash-card-pending-label

# The review queue is counted to 1000 (#1400 D10). Past it the API sets
# counts.pendingMySignatureIsLowerBound and the card reads "1000+", the same
# reading the record list's pager gives a capped total.
tracking-dash-card-pending-figure:
  type: Section
  spatial:
    display: flex
    direction: row
    align: baseline
  children:
    - tracking-dash-card-pending-count
    - tracking-dash-card-pending-count-capped

tracking-dash-card-pending-count:
  type: Text
  bind:
    content: dashboard.counts.pendingMySignature
  tokens:
    font_size: $font.size.2xl
    font_family: $font.family.mono

tracking-dash-card-pending-count-capped:
  type: Text
  content: $t.tracking.dashboard.count_lower_bound_suffix
  visible_when: dashboard.counts.pendingMySignatureIsLowerBound == true
  tokens:
    font_size: $font.size.2xl
    font_family: $font.family.mono

tracking-dash-card-pending-label:
  type: Text
  content: $t.tracking.dashboard.pending_signature
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tracking-stat-review:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.xs
    border_inline_start_accent: caution
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.md
    border: $border.default
  children:
    - tracking-stat-review-count
    - tracking-stat-review-label

tracking-stat-review-count:
  type: Text
  bind:
    content: dashboard.counts.myEntriesPendingReview
  tokens:
    font_size: $font.size.2xl
    font_family: $font.family.mono

tracking-stat-review-label:
  type: Text
  content: $t.tracking.dashboard.records_pending_review
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tracking-stat-completed:
  type: Card
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.xs
    border_inline_start_accent: go
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.md
    border: $border.default
  children:
    - tracking-stat-completed-count
    - tracking-stat-completed-label

tracking-stat-completed-count:
  type: Text
  bind:
    content: dashboard.counts.completedToday
  tokens:
    font_size: $font.size.2xl
    font_family: $font.family.mono

tracking-stat-completed-label:
  type: Text
  content: $t.tracking.dashboard.completed_today
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

# ── Pending signatures table ─────────────────────────────────────────────────

tracking-dash-pending:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.sm
  children:
    - tracking-dash-pending-title
    - tracking-dash-pending-table

tracking-dash-pending-title:
  type: Text
  content: $t.tracking.dashboard.pending_signatures
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

tracking-dash-pending-table:
  type: Table
  bind:
    rows: dashboard.pendingMySignature
  a11y:
    label: $t.tracking.dashboard.pending_table_label
  columns:
    - key: timestamp
      label: $t.tracking.dashboard.col_timestamp
      format: compliance-datetime
      width: 1.6fr
    - key: authorDisplayName
      label: $t.tracking.dashboard.col_author
      width: 1fr
    - key: formName
      label: $t.tracking.dashboard.col_form
      width: 1fr
    - key: contextDisplayName
      label: $t.tracking.dashboard.col_context
      width: 1fr
    # Status shows TrackingRecord.status's value_labels; the widest is the French
    # "En attente de revue" (about 10rem as a 600-weight badge). 1.4fr of 6fr
    # holds it at a 1280px viewport beside the sidebar (column arithmetic, not a
    # browser measurement); narrower, `truncate` clips the badge inside its cell
    # and the title tooltip carries the full label.
    - key: status
      label: $t.tracking.dashboard.col_status
      width: 1.4fr
      truncate: true
      display: badge
      variant:
        when: "$row.status == 'PendingReview'"
        value: caution
        else: info
  row_action:
    type: navigate
    route: /tracking/records/:id/sign
    params:
      id: $row.id
    # F79 — return to the dashboard after sign. origin_only (the dashboard is
    # not paginated/filterable, so there is no list state to restore — C2).
    return_context: origin_only
  empty: tracking-dash-pending-empty

tracking-dash-pending-empty:
  type: EmptyState
  content: $t.tracking.dashboard.no_pending_signatures
  body: $t.tracking.dashboard.no_pending_signatures_body

# ── My recent entries table ──────────────────────────────────────────────────

tracking-dash-recent:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.sm
  children:
    - tracking-dash-recent-title
    - tracking-dash-recent-table

tracking-dash-recent-title:
  type: Text
  content: $t.tracking.dashboard.my_recent_records
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display

tracking-dash-recent-table:
  type: Table
  bind:
    rows: dashboard.myRecentEntries
  a11y:
    label: $t.tracking.dashboard.recent_table_label
  columns:
    - key: timestamp
      label: $t.tracking.dashboard.col_timestamp
      format: compliance-datetime
      width: 1.7fr
    - key: formName
      label: $t.tracking.dashboard.col_form
      width: 1fr
    - key: contextDisplayName
      label: $t.tracking.dashboard.col_context
      width: 1fr
    # Sized and clipped as the pending table's status column above.
    - key: status
      label: $t.tracking.dashboard.col_status
      width: 1.3fr
      truncate: true
      display: badge
      variant:
        when: "$row.status == 'Complete'"
        value: go
        else:
          when: "$row.status == 'PendingReview'"
          value: caution
          else:
            when: "$row.status == 'Amended'"
            value: stop
            else: info
  row_action:
    type: navigate
    route: /tracking/records/:id
    params:
      id: $row.id
  empty: tracking-dash-recent-empty

tracking-dash-recent-empty:
  type: EmptyState
  content: $t.tracking.dashboard.no_recent_records
  body: $t.tracking.dashboard.no_recent_records_body
